import { kebabCase } from 'lodash';
import {
  ChangeEvent,
  ComponentPropsWithRef,
  FocusEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { formatTestId } from '../../../data/utils/string.utils';
import { Spinner } from '../../spinner/spinner';
import { BaseInput, BaseInputIcon, BaseInputSize } from './base-input';

export type InputSelectItem<T> = {
  key: T;
  title: JSX.Element | string | number;
  disabled?: boolean;
};

type InputSelectProps<T> = Omit<
  ComponentPropsWithRef<typeof BaseInput>,
  'value' | 'onChange' | 'onBlur' | 'ref' | 'onPaste'
> & {
  className?: string;
  label?: JSX.Element | string;
  description?: JSX.Element | string;
  prefix?: JSX.Element | string;
  suffix?: JSX.Element | string;
  icon?: BaseInputIcon;
  error?: string | { error: string; [key: string]: string };
  value?: T;
  options: InputSelectItem<T>[];
  loading?: boolean;
  onChange?: (value: T) => void;
  onBlur?: (value: T) => void;
};

export function InputSelect<T>({
  testId,
  className,
  label,
  description,
  prefix,
  suffix,
  icon = BaseInputIcon.SELECT,
  size = BaseInputSize.DEFAULT,
  disabled = false,
  error,
  options = [],
  placeholder = '',
  loading,
  onChange,
  onBlur,
  ...props
}: InputSelectProps<T>) {
  const intl = useIntl();

  const getTestId = useCallback(
    (testIdType: string | string[] | null = null) =>
      formatTestId('input-select', testId ?? props.name, testIdType),
    [testId, props.name]
  );

  const [selected, setSelected] = useState<string>('');

  useEffect(() => {
    setSelected(props.value as string);
  }, [props.value]);

  const onHandleChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      setSelected(e.currentTarget.value);

      onChange?.(
        (options.find(option => String(option.key) === e.currentTarget.value)
          ?.key as T) ?? (null as T)
      );
    },
    [options, onChange]
  );

  const onHandleBlur = useCallback(
    (e: FocusEvent<HTMLSelectElement>) => {
      onBlur?.(
        (options.find(option => String(option.key) === e.currentTarget.value)
          ?.key as T) ?? (null as T)
      );
    },
    [options, onBlur]
  );

  return (
    <div
      data-test-id={getTestId()}
      className={`form-group form-group--select${
        className ? ` form-group--${className}` : ''
      }${error ? ' invalid' : ''}${disabled ? ' disabled' : ''}`}
    >
      {label && (
        <label data-test-id={getTestId('label')} htmlFor={props.name}>
          {label}
        </label>
      )}

      {description && (
        <div
          data-test-id={getTestId('description')}
          className="input-description"
        >
          {description}
        </div>
      )}

      <div className="input-group">
        {prefix && (
          <span
            data-test-id={getTestId('prefix')}
            className="form-control-prefix"
          >
            {prefix}
          </span>
        )}
        {suffix && (
          <span
            data-test-id={getTestId('suffix')}
            className="form-control-suffix"
          >
            {suffix}
          </span>
        )}

        {icon !== BaseInputIcon.NONE && loading !== true && (
          <i
            data-test-id={getTestId('icon')}
            className={`icon ${kebabCase(icon)}`}
          />
        )}

        {loading && <Spinner />}

        <select
          {...props}
          data-test-id={getTestId('select')}
          id={props.name}
          disabled={disabled}
          className={`form-control${
            size !== BaseInputSize.DEFAULT ? ` form-control--${size}` : ``
          }`}
          value={selected as string}
          onChange={onHandleChange}
          onBlur={onHandleBlur}
        >
          {placeholder !== '' && (
            <option
              data-test-id={getTestId(['select', 'placeholder'])}
              value=""
            >
              {placeholder}
            </option>
          )}

          {options.map(option => (
            <option
              data-test-id={getTestId(['select', option.key as string])}
              key={option.key as string}
              value={option.key as string}
              disabled={option.disabled === true}
            >
              {option.title}
            </option>
          ))}
        </select>

        {error && (
          <div data-test-id={getTestId('error')} className="input-error">
            {intl.formatMessage(
              {
                id: typeof error === 'string' ? error : error.error,
              },
              typeof error === 'string' ? {} : error
            )}
          </div>
        )}
      </div>
    </div>
  );
}
